import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gameLogger } from '../../../../utils/GameLogger';
import { ITEM_DATABASE } from '../../../../shared/content/generated/items.gen';
import type { ItemEquipmentSlot } from '../../../../shared/equipmentSlots';
import { useGame } from '../../contexts/GameContext';
import { CharacterPortrait } from '../../components/CharacterPortrait';
import { TomeButton, TomeChapter, TomeFolio } from '../../primitives/tome';
import { useEquipment } from '../../hooks/useEquipment';
import { useInventory } from '../../hooks/useInventory';
import { formatCallingWithArchetype } from '../../theme';
import { CharacterEquipmentSlot } from './CharacterEquipmentSlot';
import {
  EQUIPMENT_SLOTS, compatibleInventoryItems, equipBlock, slotDefinition, itemEquipmentSlot,
  readDraggedItemId, dropToast, rarityTone, humanize, formatItemStat,
  type EquipmentSlotDefinition, type EquipmentDropState, type DraggedInventoryItem,
} from './characterPresentation';

export const EquipmentContent: React.FC = () => {
  const { player } = useGame();
  const { slots, equip, unequip } = useEquipment();
  const { items: inventoryItems } = useInventory();
  const [selectedSlot, setSelectedSlot] = useState<ItemEquipmentSlot>('weapon');
  const [draggedItem, setDraggedItem] = useState<DraggedInventoryItem | null>(null);
  const [dropTarget, setDropTarget] = useState<ItemEquipmentSlot | 'character' | null>(null);

  const selectedItem = slots[selectedSlot] ?? null;
  const selectedCanonicalItem = selectedItem ? ITEM_DATABASE[selectedItem.id] : undefined;
  const selectedDefinition = slotDefinition(selectedSlot);
  // What fits comes first; the rest stays listed, saying why it cannot be worn.
  const compatibleItems = useMemo(
    () => compatibleInventoryItems(inventoryItems, selectedSlot)
      .map(item => ({ item, block: equipBlock(item.id, player) }))
      .sort((a, b) => Number(a.block !== null) - Number(b.block !== null)),
    [inventoryItems, selectedSlot, player],
  );
  const folioRef = useRef<HTMLElement>(null);
  const previewReady = player?.appearance != null && player.equipmentVisuals != null;

  useEffect(() => {
    const handleGlobalDragStart = (event: Event) => {
      const detail = (event as CustomEvent<{ type?: string; itemId?: string }>).detail;
      if (detail?.type !== 'item' || !detail.itemId) {
        setDraggedItem(null);
        return;
      }
      const item = ITEM_DATABASE[detail.itemId];
      setDraggedItem({
        itemId: detail.itemId,
        name: item?.name ?? detail.itemId,
        equipmentSlot: itemEquipmentSlot(detail.itemId),
      });
    };
    const handleGlobalDragEnd = () => {
      setDraggedItem(null);
      setDropTarget(null);
    };

    window.addEventListener('helix:action-drag-start', handleGlobalDragStart);
    window.addEventListener('dragend', handleGlobalDragEnd);
    return () => {
      window.removeEventListener('helix:action-drag-start', handleGlobalDragStart);
      window.removeEventListener('dragend', handleGlobalDragEnd);
    };
  }, []);

  const resetDragState = () => {
    setDraggedItem(null);
    setDropTarget(null);
  };

  const equipRequestedItem = (itemId: string, expectedSlot?: ItemEquipmentSlot) => {
    const item = ITEM_DATABASE[itemId];
    const actualSlot = itemEquipmentSlot(itemId);
    if (!item || !actualSlot) {
      dropToast('Only equippable gear can go on the character screen');
      resetDragState();
      return;
    }
    if (expectedSlot && actualSlot !== expectedSlot) {
      dropToast(`${item.name} belongs in the ${slotDefinition(actualSlot).label.toLowerCase()} slot`);
      resetDragState();
      return;
    }

    const block = equipBlock(itemId, player);
    if (block) {
      dropToast(`${item.name}: ${block}`);
      resetDragState();
      return;
    }

    gameLogger.debug('ui', `[Equipment] Requesting equip for ${itemId} in ${actualSlot}`);
    equip(itemId);
    resetDragState();
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLElement>,
    target: ItemEquipmentSlot | 'character',
  ) => {
    if (!draggedItem) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = draggedItem.equipmentSlot ? 'copy' : 'none';
    setDropTarget(target);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    const related = event.relatedTarget;
    if (!(related instanceof Node) || !event.currentTarget.contains(related)) {
      setDropTarget(null);
    }
  };

  const handleDrop = (
    event: React.DragEvent<HTMLElement>,
    expectedSlot?: ItemEquipmentSlot,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('helix:action-drag-end'));
    }
    const itemId = readDraggedItemId(event.dataTransfer) ?? draggedItem?.itemId ?? null;
    if (!itemId) {
      resetDragState();
      return;
    }
    equipRequestedItem(itemId, expectedSlot);
  };

  const requestUnequip = () => {
    if (!selectedItem) return;
    gameLogger.debug('ui', `[Equipment] Requesting unequip for ${selectedSlot}`);
    unequip(selectedSlot);
  };

  const selectSlot = (slotId: ItemEquipmentSlot) => {
    setSelectedSlot(slotId);
    // Beside the slots this does nothing; on a stacked page it brings the answer to the tap.
    folioRef.current?.scrollIntoView?.({ block: 'nearest' });
  };

  const characterDropState: EquipmentDropState = !draggedItem
    ? 'idle'
    : !draggedItem.equipmentSlot
      ? 'invalid'
      : dropTarget === 'character'
        ? 'valid'
        : 'available';

  const equipmentSlotDropState = (slotId: ItemEquipmentSlot): EquipmentDropState => {
    if (!draggedItem) return 'idle';
    if (draggedItem.equipmentSlot !== slotId) return 'invalid';
    return dropTarget === slotId ? 'valid' : 'available';
  };

  const itemStats = Object.entries(selectedCanonicalItem?.stats ?? selectedItem?.stats ?? {})
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number');
  const requirementLevel = selectedCanonicalItem?.requiredLevel;
  const requirementClass = selectedCanonicalItem?.requiredClass;

  const renderSlot = (definition: EquipmentSlotDefinition) => (
    <CharacterEquipmentSlot
      key={definition.id}
      definition={definition}
      item={slots[definition.id] ?? null}
      selected={selectedSlot === definition.id}
      dropState={equipmentSlotDropState(definition.id)}
      onSelect={() => selectSlot(definition.id)}
      onDragEnter={event => handleDragOver(event, definition.id)}
      onDragOver={event => handleDragOver(event, definition.id)}
      onDragLeave={handleDragLeave}
      onDrop={event => handleDrop(event, definition.id)}
    />
  );

  return (
    <section className="character-equipment" aria-label="Equipment">
      <div
        className="character-equipment__scroll"
        data-character-scroll-owner="equipment"
        tabIndex={0}
        aria-label="Equipment sheet"
      >
        <div className="character-equipment__layout">
          <div className="character-equipment__slots character-equipment__slots--left">
            {EQUIPMENT_SLOTS.filter(slot => slot.side === 'left').map(renderSlot)}
          </div>

          <section
            className="character-preview"
            aria-label="Live character preview and general equipment drop target"
            data-drop-state={characterDropState}
            onDragEnter={event => handleDragOver(event, 'character')}
            onDragOver={event => handleDragOver(event, 'character')}
            onDragLeave={handleDragLeave}
            onDrop={event => handleDrop(event)}
          >
            <div className="character-preview__stage">
              {previewReady ? (
                <CharacterPortrait
                  appearance={player.appearance!}
                  characterClass={player.class}
                  label={`${player.name}, current appearance and equipped gear`}
                  equipmentVisuals={player.equipmentVisuals!}
                  size={220}
                />
              ) : (
                <div className="character-preview__unavailable" role="status">
                  <strong>Preview unavailable</strong>
                  <span>Waiting for authoritative appearance and equipment visuals.</span>
                </div>
              )}
            </div>
            <div className="character-preview__drop-copy">
              {characterDropState === 'valid'
                ? `Release ${draggedItem?.name ?? 'gear'} to request equip`
                : characterDropState === 'invalid'
                  ? 'This item is not equippable gear'
                  : 'Drop gear here, or choose a slot'}
            </div>
          </section>

          <div className="character-equipment__slots character-equipment__slots--right">
            {EQUIPMENT_SLOTS.filter(slot => slot.side === 'right').map(renderSlot)}
          </div>

          <div className="character-equipment__slots character-equipment__slots--center">
            {EQUIPMENT_SLOTS.filter(slot => slot.side === 'center').map(renderSlot)}
          </div>
        </div>

        <TomeFolio ref={folioRef} className="character-item-folio" aria-labelledby="character-selected-slot-heading" shapeSeed={selectedSlot}>
          <div className="character-item-detail">
            <div className="character-item-detail__summary">
              <p className="character-eyebrow">{selectedDefinition.label} slot</p>
              <h3 id="character-selected-slot-heading">{selectedItem?.name ?? 'Empty slot'}</h3>
              {selectedItem ? (
                <>
                  <span
                    className="character-item-detail__rarity"
                    style={{ '--character-slot-tone': rarityTone(selectedItem) } as React.CSSProperties}
                  >
                    {humanize(selectedCanonicalItem?.rarity ?? selectedItem.rarity ?? 'rarity unavailable')}
                  </span>
                  <p>{selectedCanonicalItem?.description ?? selectedItem.description ?? 'No description available.'}</p>
                </>
              ) : (
                <p>Nothing is equipped here. Gear from your bag that fits this slot is listed below.</p>
              )}
            </div>

            <TomeChapter title="Item facts" className="character-item-detail__facts">
              {selectedItem ? (
                <>
                  <div className="character-item-detail__fact-grid">
                    {itemStats.length > 0 ? itemStats.map(([stat, value]) => (
                      <div key={stat}>
                        <span>{humanize(stat)}</span>
                        <strong>{formatItemStat(value)}</strong>
                      </div>
                    )) : <p>No numeric item bonuses.</p>}
                  </div>
                  <ul className="character-item-detail__requirements" aria-label="Item requirements">
                    {requirementLevel && requirementLevel > 1 ? (
                      <li data-requirement-status={player && player.level >= requirementLevel ? 'met' : 'unmet'}>
                        Requires level {requirementLevel}
                      </li>
                    ) : null}
                    {requirementClass ? (
                      <li data-requirement-status={player?.class === requirementClass ? 'met' : 'unmet'}>
                        Requires {formatCallingWithArchetype(requirementClass)} class
                      </li>
                    ) : null}
                    {selectedCanonicalItem
                      && (!requirementLevel || requirementLevel <= 1)
                      && !requirementClass ? (
                        <li>No special requirements</li>
                      ) : null}
                    {!selectedCanonicalItem ? <li>Requirements unavailable</li> : null}
                  </ul>
                </>
              ) : <p>Select inventory gear below to request an equip.</p>}
            </TomeChapter>

            <TomeChapter title="Compatible inventory gear" className="character-item-detail__actions">
              <div className="character-compatible-items">
                {compatibleItems.length > 0 ? compatibleItems.map(({ item, block }, index) => (
                  <div key={`${item.id}-${index}`}>
                    <TomeButton
                      tone="secondary"
                      cut="clean"
                      fullWidth
                      disabled={block !== null}
                      aria-label={`Equip ${item.name} in ${selectedDefinition.label} slot`}
                      onClick={() => equipRequestedItem(item.id, selectedSlot)}
                    >
                      Equip {item.name}
                    </TomeButton>
                    {block && <small>{block}</small>}
                  </div>
                )) : <p>No compatible gear in your bag.</p>}
              </div>
              <TomeButton
                className="character-item-detail__unequip"
                tone="secondary"
                cut="clean"
                disabled={!selectedItem}
                onClick={requestUnequip}
              >
                {selectedItem ? `Unequip ${selectedItem.name}` : 'Nothing to unequip'}
              </TomeButton>
              <p className="character-item-detail__authority-note">
                Your gear updates after the realm confirms the change.
              </p>
            </TomeChapter>
          </div>
        </TomeFolio>
      </div>
    </section>
  );
};
